import { useEffect, useState, useRef, useCallback } from 'react';
import { Socket } from 'socket.io-client';
import type { CookingRecipe } from '../../../server/src/skills/CookingSkillSystem';

export interface CookingState {
  level: number;
  xp: number;
  nextLevelXp: number;
  isCooking: boolean;
  currentRecipe: CookingRecipe | null;
  itemsCooked: number;
  batchSize: number;
  nextCookTime: number | undefined;
}

export interface UseCookingReturn {
  cookingState: CookingState;
  recipes: CookingRecipe[];
  startCooking: (recipeId: string, quantity: number) => void;
  stopCooking: () => void;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to manage cooking skill progression and active cooking sessions
 */
export const useCooking = (socketRef: React.MutableRefObject<Socket | null>): UseCookingReturn => {
  const [cookingState, setCookingState] = useState<CookingState>({
    level: 1,
    xp: 0,
    nextLevelXp: 0,
    isCooking: false,
    currentRecipe: null,
    itemsCooked: 0,
    batchSize: 0,
    nextCookTime: undefined,
  });

  const [recipes, setRecipes] = useState<CookingRecipe[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

   // Fetch available recipes and player cooking stats
   const fetchCookingData = useCallback(() => {
     if (!socketRef.current || !socketRef.current.connected) {
       console.log('❌ Socket not connected, skipping recipe fetch');
       return;
     }

     console.log('🔄 Fetching cooking data...');
     setIsLoading(true);
     
     // Set a timeout to stop loading if no response
     const timeout = setTimeout(() => {
       console.warn('⚠️ Cooking data fetch timeout - no response from server');
       setIsLoading(false);
     }, 3000);
     
     // Request recipes
     socketRef.current.emit('cooking:getRecipes', {});
     
     // Request stats
     socketRef.current.emit('cooking:getStats', {});

     return () => clearTimeout(timeout);
   }, [socketRef]);

  // Update cooking progress regularly
  const startProgressUpdates = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }

    progressIntervalRef.current = setInterval(() => {
      if (!socketRef.current) return;
      socketRef.current.emit('cooking:getProgress', {});
    }, 500); // Update every 500ms
  }, [socketRef]);

  const stopProgressUpdates = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  // Set up socket event listeners
  useEffect(() => {
    if (!socketRef.current) return;

    // Receive recipes
    const handleRecipes = (data: { recipes: CookingRecipe[]; playerLevel: number }) => {
      console.log(`🍳 Received cooking recipes - Count: ${data.recipes?.length || 0}, Level: ${data.playerLevel}`);
      if (data.recipes && data.recipes.length > 0) {
        console.log(`   First recipe: ${data.recipes[0].name} (Level ${data.recipes[0].requiredLevel})`);
      }
      setRecipes(data.recipes);
      setIsLoading(false);
    };

    // Receive stats
    const handleStats = (data: {
      level: number;
      totalXp: number;
      currentXp: number;
      nextLevelXp: number;
      isCooking: boolean;
      currentRecipe: CookingRecipe | null;
      itemsCooked: number;
      batchSize: number;
      nextCookTime: number | undefined;
    }) => {
      setCookingState({
        level: data.level,
        xp: data.currentXp,
        nextLevelXp: data.nextLevelXp,
        isCooking: data.isCooking,
        currentRecipe: data.currentRecipe,
        itemsCooked: data.itemsCooked,
        batchSize: data.batchSize,
        nextCookTime: data.nextCookTime,
      });
      setIsLoading(false);
    };

    // Receive progress updates
    const handleProgress = (data: {
      isCooking: boolean;
      currentRecipe: CookingRecipe | null;
      itemsCooked: number;
      batchSize: number;
      nextCookTime: number | undefined;
      level?: number;
      currentXp?: number;
      nextLevelXp?: number;
    }) => {
      setCookingState(prev => ({
        ...prev,
        level: data.level ?? prev.level,
        xp: data.currentXp ?? prev.xp,
        nextLevelXp: data.nextLevelXp ?? prev.nextLevelXp,
        isCooking: data.isCooking,
        currentRecipe: data.currentRecipe,
        itemsCooked: data.itemsCooked,
        batchSize: data.batchSize,
        nextCookTime: data.nextCookTime,
      }));
    };

    // Handle cooking started
    const handleStarted = (data: { recipeId: string; quantity: number }) => {
      startProgressUpdates();
    };

    // Handle cooking stopped
    const handleStopped = (data: { itemsCooked: string }) => {
      stopProgressUpdates();
      setCookingState(prev => ({
        ...prev,
        isCooking: false,
        currentRecipe: null,
        itemsCooked: 0,
        batchSize: 0,
      }));
    };

    // Handle errors
    const handleError = (data: { message: string }) => {
      setError(data.message);
      stopProgressUpdates();
    };

    socketRef.current.on('cooking:recipes', handleRecipes);
    socketRef.current.on('cooking:stats', handleStats);
    socketRef.current.on('cooking:progress', handleProgress);
    socketRef.current.on('cooking:started', handleStarted);
    socketRef.current.on('cooking:stopped', handleStopped);
    socketRef.current.on('cooking:error', handleError);

      const socket = socketRef.current;
      return () => {
        if (!socket) return;
        socket.off('cooking:recipes', handleRecipes);
        socket.off('cooking:stats', handleStats);
        socket.off('cooking:progress', handleProgress);
        socket.off('cooking:started', handleStarted);
        socket.off('cooking:stopped', handleStopped);
        socket.off('cooking:error', handleError);
        stopProgressUpdates();
      };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startProgressUpdates, stopProgressUpdates]);

     // Fetch initial data on mount and whenever socket becomes available
     // eslint-disable-next-line react-hooks/exhaustive-deps
     useEffect(() => {
       const attemptFetch = () => {
         if (!socketRef.current?.connected) {
           // Socket not ready, try again soon
           console.log('⏳ Socket not connected, retrying in 100ms...');
           const timer = setTimeout(attemptFetch, 100);
           return () => clearTimeout(timer);
         }
         
         console.log('✅ Socket connected, fetching cooking data');
         fetchCookingData();
       };

       return attemptFetch();
      }, [fetchCookingData]); // eslint-disable-line react-hooks/exhaustive-deps

    // Start cooking
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const startCooking = useCallback(
      (recipeId: string, quantity: number) => {
        if (!socketRef.current) return;
        setError(null);
        socketRef.current.emit('cooking:start', { recipeId, quantity });
      },
      [] // eslint-disable-line react-hooks/exhaustive-deps
    );

    // Stop cooking
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const stopCooking = useCallback(() => {
      if (!socketRef.current) return;
      socketRef.current.emit('cooking:stop', {});
    }, [] // eslint-disable-line react-hooks/exhaustive-deps
    );

  return {
    cookingState,
    recipes,
    startCooking,
    stopCooking,
    isLoading,
    error,
  };
};
